import {
  TrainingPartnersApi,
  type TrainingPartnerListItem,
  type TrainingPartnerListMeta,
  type TrainingPartnerModel,
  type TrainingPartnerPaginationMeta,
} from "@simbelapp/order-sdk";
import { defineStore } from "pinia";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { usePagination } from "~/composables/paginations/usePagination";
import type { ITrainingPartnersStore } from "~/utils/interfaces/training-partners-interfaces";

export const useTrainingPartnersStore = defineStore("TrainingPartnersStore", {
  state: (): ITrainingPartnersStore => {
    return {
      trainingPartners: [],
      showEditTrainingPartner: false,
      editTrainingPartner: {
        id: null,
        name: null,
        mainContactEmail: null,
        mainContactFirstname: null,
        mainContactLastname: null,
        logo: null,
        legalNotice: null,
      },
    };
  },
  getters: {},
  actions: {
    async create(body: { name: string }) {
      const feedback = useFeedback();

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingPartnersApi);
        return await apiInstance.createOrGetOwnPartner({
          createOwnPartnerDto: { partnerName: body.name },
        });
      } catch {
        feedback.error(`erreur`, "small");
      }
    },

    async searchTrainingPartners(
      params: Record<string, any> = {},
    ): Promise<{ data: TrainingPartnerListItem[]; meta: TrainingPartnerListMeta | null }> {
      const feedback = useFeedback();

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingPartnersApi);
        const response = await apiInstance.getTrainingPartners({ ...params, lazyLoad: true });
        return { data: response.data ?? [], meta: response.meta };
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
        return { data: [], meta: null };
      }
    },

    /**
     * Partners a company may pick from: Simbel partners not yet linked to any company, plus its own.
     * Narrower than searchTrainingPartners, which lists every Simbel partner whoever owns it.
     */
    async searchCompanyPartners(
      params: Record<string, any> = {},
    ): Promise<{ data: TrainingPartnerModel[]; meta: TrainingPartnerPaginationMeta | null }> {
      const feedback = useFeedback();

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingPartnersApi);
        const response = await apiInstance.findCompanyPartners({ ...params, lazyLoad: true });
        return { data: response.data ?? [], meta: response.meta };
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
        return { data: [], meta: null };
      }
    },

    async updateMainContact(body: { partnerId: string; email: string }) {
      const feedback = useFeedback();

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingPartnersApi);
        await apiInstance.updateMainContact({
          updateMainContactDto: {
            partnerId: body.partnerId,
            email: body.email,
          },
        });
      } catch {
        feedback.error(`erreur`, "small");
      }
    },

    async fetchAllTrainingPartners() {
      const feedback = useFeedback();
      const pagination = usePagination();

      if (pagination.paginateOptions.value?.filter === "Organismes") pagination.paginateOptions.value.filter = null;

      const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingPartnersApi);
        this.trainingPartners = await apiInstance.getTrainingPartners({
          search: paginateOptions.search,
          offset: paginateOptions.offset,
          limit: paginateOptions.limit,
          sortingColumn: paginateOptions.sortingColumn,
          order: paginateOptions.order,
          lazyLoad: true,
        });
      } catch {
        feedback.error(`Une erreur est survenue`, "small");
      }
    },

    // Done to avoid an impact on fetchAllTrainingPartners
    async fetchTrainingPartnersLazyWithStore(search?: string) {
      const { data } = await this.searchTrainingPartners({ limit: 10, search });
      this.trainingPartners = data;
    },

    async findOneTrainingPartner(partnerId: string) {
      const feedback = useFeedback();

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingPartnersApi);
        return await apiInstance.getTrainingPartnerById({ trainingPartnerId: partnerId });
      } catch {
        feedback.error(`erreur`, "small");
      }
    },
    init() {
      this.editTrainingPartner.mainContactEmail = null;
      this.editTrainingPartner.name = null;
      this.editTrainingPartner.mainContactFirstname = null;
      this.editTrainingPartner.mainContactLastname = null;
      this.editTrainingPartner.logo = null;
      this.editTrainingPartner.legalNotice = null;
    },
    async openEditTrainingPartner(partnerId: string) {
      this.showEditTrainingPartner = true;

      const partner = await this.findOneTrainingPartner(partnerId);
      if (!partner) return;

      this.editTrainingPartner.name = partner.name;
      this.editTrainingPartner.mainContactEmail = partner.mainContactEmail;
      this.editTrainingPartner.mainContactFirstname = partner.mainContactFirstname;
      this.editTrainingPartner.mainContactLastname = partner.mainContactLastname;
      this.editTrainingPartner.logo = partner.logo;
      this.editTrainingPartner.legalNotice = partner.legalNotice;
      this.editTrainingPartner.id = partnerId;
    },
    closeModal() {
      this.init();

      this.showEditTrainingPartner = false;
    },
    async convertTrainingPartnerLogo(logo: File) {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingPartnersApi);
      const response = await apiInstance.convertTrainingPartnersLogo({
        file: logo,
      });

      const convertedImg = await response;
      if (convertedImg) {
        this.editTrainingPartner.logo = new File([convertedImg], logo.name, { type: "image/png" });
      }
    },
    async updateTrainingPartnerLogo(logo: File) {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingPartnersApi);
      if (this.editTrainingPartner.id) {
        await apiInstance.updateTrainingPartnerLogo({
          trainingPartnerId: this.editTrainingPartner.id,
          file: logo,
        });
      }
    },

    async updateTrainingPartnerSimbelAdmin(trainingPartnerId: string) {
      const feedback = useFeedback();

      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, TrainingPartnersApi);
      try {
        if (this.editTrainingPartner.logo && this.editTrainingPartner.logo instanceof File) {
          await this.convertTrainingPartnerLogo(this.editTrainingPartner.logo as File);
          await this.updateTrainingPartnerLogo(this.editTrainingPartner.logo as File);
        }
        if (this.editTrainingPartner.name) {
          apiInstance.updatePartnerInfos({
            trainingPartnerId,
            updateTrainingPartnerInfosDto: {
              name: this.editTrainingPartner.name,
              mainContactEmail: this.editTrainingPartner.mainContactEmail,
              mainContactFirstname: this.editTrainingPartner.mainContactFirstname,
              mainContactLastname: this.editTrainingPartner.mainContactLastname,
              legalNotice: this.editTrainingPartner.legalNotice,
            },
          });

          this.fetchAllTrainingPartners();
          // this.closeModal();
          feedback.success(`L’organisme a bien été mis à jour`, "small");
        }
      } catch {
        feedback.error(`erreur`, "small");
      }
    },
  },
});
